/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Component } from '@appCanvasCommon/component-system/Component';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { Interaction } from 'src/app/plain-javascript/services/interaction-blocker';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

interface IHoverInteractionEvent {
	global: {
		x: number;
		y: number;
	};
}

const config = {
	mouseEvents: {
		mouseOver: `mouseover`,
		mouseOut: `mouseout`,
	},
};

/**
 * A class that detects "mouseover" and "mouseout" events over Tickets.
 */
export class TicketHover extends Component implements ComponentInterface {
	private _canvasAngularLand: CanvasAngularLand;

	constructor() {
		super();
	}

	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 */
	public override setup(canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer): void {
		this._canvasAngularLand = canvasAngularLand;

		const parent = this.getParent();
		parent.eventMode = EventMode.Static;

		parent.on(config.mouseEvents.mouseOver, (event: IHoverInteractionEvent) =>
			this.handleMouseOver(dataContainer, event)
		);

		parent.on(config.mouseEvents.mouseOut, (_: IHoverInteractionEvent) => this.handleMouseOut());
	}

	private handleMouseOver(dataContainer: TicketContainer, event: IHoverInteractionEvent): void {
		if (this._canvasAngularLand.interactionBlocker.check(Interaction.DisplayTicketHoverInfo)) {
			const hoveredTicket = dataContainer.getData();

			this._canvasAngularLand.hoverOverStateService.hoverPosition$.next({
				x: event.global.x,
				y: event.global.y,
			});

			const selectedTicketsMap = this._canvasAngularLand.tickets.editTicket$.getValue();
			const isTicketSelected = selectedTicketsMap.get(hoveredTicket.$id);

			if (!(selectedTicketsMap.size === 1 && isTicketSelected)) {
				this._canvasAngularLand.hoverOverStateService.updateId(hoveredTicket.$id);
				this._canvasAngularLand.hoverOverStateService.startHover();
			}
		}
	}

	private handleMouseOut(): void {
		this._canvasAngularLand.hoverOverStateService.endHover();
	}

	public override destroy(): void {
		const parent = this.getParent();
		parent.off(config.mouseEvents.mouseOut, this.handleMouseOut);
		parent.off(config.mouseEvents.mouseOver, this.handleMouseOver);

		super.destroy();
	}
}
